import { useTranslation } from 'react-i18next';

interface TimeData {
  avg_seconds_per_question: number;
  by_difficulty: Record<string, number>;
  by_topic: { topic: string; avg_seconds: number }[];
}

interface Props {
  data: TimeData;
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

export default function TimeAnalyticsPanel({ data }: Props) {
  const { t } = useTranslation();
  const maxTime = Math.max(...data.by_topic.map((tp) => tp.avg_seconds), 1);

  return (
    <div className="space-y-4">
      {/* Global average */}
      <div className="flex items-center gap-3">
        <span className="text-2xl">⏱️</span>
        <div>
          <p className="font-mono text-lg font-bold text-slate-900 dark:text-slate-200">{formatTime(data.avg_seconds_per_question)}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('dashboard.time.avgPerQuestion')}</p>
        </div>
      </div>

      {/* By difficulty */}
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(data.by_difficulty).map(([level, avg]) => (
          <div key={level} className="rounded-xl border border-slate-200/70 bg-slate-50/60 p-2 text-center dark:border-white/10 dark:bg-white/5">
            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">{level}</p>
            <p className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-200">{formatTime(avg)}</p>
          </div>
        ))}
      </div>

      {/* By topic */}
      {data.by_topic.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{t('dashboard.time.byTopic')}</p>
          {data.by_topic.slice(0, 8).map((tp) => (
            <div key={tp.topic}>
              <div className="flex justify-between text-xs mb-0.5">
                <span className="text-slate-700 dark:text-slate-300 truncate max-w-[60%]">{tp.topic}</span>
                <span className="font-mono text-slate-500 dark:text-slate-400">{formatTime(tp.avg_seconds)}</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10">
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                  style={{ width: `${(tp.avg_seconds / maxTime) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
